import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  X, Plus, Play, Pause, Trash2, Clock, StickyNote, Bold, Italic, List,
} from "lucide-react";
import { useEpisodeNotes, type EpisodeNote } from "@/hooks/useEpisodeNotes";

interface NoteEditorModalProps {
  open: boolean;
  onClose: () => void;
  episodeId: string;
  episodeTitle: string;
  audioUrl?: string | null;
  durationSeconds?: number | null;
  podcastName?: string;
  artworkUrl?: string | null;
  /** Pre-fill timestamp if audio was playing when opened */
  initialTimestamp?: number | null;
}

function formatTimestamp(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function NoteEditorModal({
  open,
  onClose,
  episodeId,
  episodeTitle,
  audioUrl,
  durationSeconds,
  podcastName,
  artworkUrl,
  initialTimestamp,
}: NoteEditorModalProps) {
  const { notes, loading, addNote, deleteNote } = useEpisodeNotes(open ? episodeId : undefined);

  // View state: "library" (existing notes list) or "add" (new note form)
  const [view, setView] = useState<"library" | "add">("library");
  const [content, setContent] = useState("");
  const [timestamp, setTimestamp] = useState<number | null>(null);
  const [wantsTimestamp, setWantsTimestamp] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inline audio player for timestamp picking
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playerTime, setPlayerTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(durationSeconds || 0);
  const [playerPlaying, setPlayerPlaying] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setContent("");
      setTimestamp(initialTimestamp ?? null);
      setWantsTimestamp(!!initialTimestamp);
      setPlayerTime(0);
      setPlayerPlaying(false);
    }
  }, [open, initialTimestamp]);

  // Auto-switch to "add" view if no existing notes
  useEffect(() => {
    if (!loading && open) {
      setView(notes.length > 0 ? "library" : "add");
    }
  }, [loading, notes.length, open]);

  const togglePlayer = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playerPlaying) {
      a.pause();
    } else {
      a.play().catch(() => {});
    }
    setPlayerPlaying(!playerPlaying);
  }, [playerPlaying]);

  const seekTo = (fraction: number) => {
    const a = audioRef.current;
    if (!a || !playerDuration) return;
    a.currentTime = fraction * playerDuration;
    setPlayerTime(a.currentTime);
  };

  const handleSaveTimestamp = () => {
    setTimestamp(Math.floor(playerTime));
    // Pause player after saving timestamp
    audioRef.current?.pause();
    setPlayerPlaying(false);
  };

  const handleSaveNote = async () => {
    if (!content.trim()) return;
    setSaving(true);
    await addNote(content.trim(), wantsTimestamp ? timestamp : null);
    setSaving(false);
    setContent("");
    setTimestamp(null);
    setWantsTimestamp(false);
    setView("library");
  };

  const insertFormatting = (before: string, after: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = content.substring(start, end);
    const newText = content.substring(0, start) + before + selected + after + content.substring(end);
    setContent(newText);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center px-0 sm:px-4">
      <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div
        className="relative w-full sm:max-w-lg bg-card border-[3px] border-foreground animate-scale-in max-h-[85vh] flex flex-col rounded-t-lg sm:rounded-md overflow-hidden"
        style={{ boxShadow: "8px 8px 0 hsl(var(--foreground))" }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b-[3px] border-foreground bg-muted/40 flex-shrink-0">
          <StickyNote className="w-5 h-5 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-mono font-bold text-base">Notes</h3>
            <p className="font-mono text-[10px] text-muted-foreground truncate">{episodeTitle}</p>
          </div>
          <button onClick={onClose} aria-label="Close notes" className="inline-flex items-center justify-center min-h-11 min-w-11 text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded" />
              ))}
            </div>
          )}

          {/* Library view */}
          {!loading && view === "library" && (
            <div className="space-y-3">
              {notes.map((note) => (
                <NoteCard key={note.id} note={note} onDelete={deleteNote} />
              ))}
              <button
                onClick={() => {
                  setView("add");
                  setContent("");
                  setTimestamp(initialTimestamp ?? null);
                  setWantsTimestamp(!!initialTimestamp);
                }}
                className="btn-comic-secondary w-full"
              >
                <Plus className="w-3.5 h-3.5" />
                Add New Note
              </button>
            </div>
          )}

          {/* Add note view */}
          {!loading && view === "add" && (
            <div className="space-y-3">
              {/* Formatting toolbar */}
              <div className="flex items-center gap-1 border-b border-border pb-2">
                <button
                  onClick={() => insertFormatting("**", "**")}
                  className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded"
                  title="Bold"
                >
                  <Bold className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => insertFormatting("*", "*")}
                  className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded"
                  title="Italic"
                >
                  <Italic className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => insertFormatting("\n- ", "")}
                  className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded"
                  title="List"
                >
                  <List className="w-3.5 h-3.5" />
                </button>
              </div>

              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={5}
                placeholder="Write your note here…"
                className="w-full text-base sm:text-lg leading-snug px-3 py-2 bg-input border-[2px] border-foreground rounded-md focus:outline-none focus:border-accent focus:shadow-[3px_3px_0px_0px_hsl(var(--accent))] transition-[border-color,box-shadow] duration-150 resize-none"
                style={{ fontFamily: "'Architects Daughter', cursive" }}
                autoFocus
              />

              {/* Timestamp section */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 font-mono text-[11px] text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wantsTimestamp}
                    onChange={(e) => setWantsTimestamp(e.target.checked)}
                    className="accent-primary"
                  />
                  <Clock className="w-3 h-3" />
                  Add timestamp
                </label>

                {wantsTimestamp && (
                  <div className="retro-border bg-muted/30 p-3 rounded space-y-2">
                    {timestamp !== null ? (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-primary">
                          ⏱ {formatTimestamp(timestamp)}
                        </span>
                        <button
                          onClick={() => setTimestamp(null)}
                          className="font-mono text-[10px] text-muted-foreground hover:text-foreground underline"
                        >
                          Change
                        </button>
                      </div>
                    ) : audioUrl ? (
                      <>
                        <audio
                          ref={audioRef}
                          src={audioUrl}
                          preload="metadata"
                          onLoadedMetadata={(e) => setPlayerDuration((e.target as HTMLAudioElement).duration)}
                          onTimeUpdate={(e) => setPlayerTime((e.target as HTMLAudioElement).currentTime)}
                          onEnded={() => setPlayerPlaying(false)}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={togglePlayer}
                            className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0"
                          >
                            {playerPlaying ? (
                              <Pause className="w-3.5 h-3.5" />
                            ) : (
                              <Play className="w-3.5 h-3.5 ml-0.5" fill="currentColor" />
                            )}
                          </button>
                          <div className="flex-1 space-y-1">
                            <div
                              className="h-2 bg-muted rounded-full cursor-pointer relative"
                              onClick={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                seekTo((e.clientX - rect.left) / rect.width);
                              }}
                            >
                              <div
                                className="absolute inset-y-0 left-0 bg-primary rounded-full"
                                style={{ width: `${playerDuration ? (playerTime / playerDuration) * 100 : 0}%` }}
                              />
                            </div>
                            <div className="flex justify-between font-mono text-[9px] text-muted-foreground">
                              <span>{formatTimestamp(playerTime)}</span>
                              <span>{playerDuration ? formatTimestamp(playerDuration) : "--:--"}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={handleSaveTimestamp}
                          className="btn-comic-secondary btn-comic-sm w-full"
                        >
                          <Clock className="w-3 h-3" />
                          Save Timestamp at {formatTimestamp(playerTime)}
                        </button>
                      </>
                    ) : (
                      <p className="font-mono text-[10px] text-muted-foreground">
                        No audio available for this episode.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleSaveNote}
                  disabled={!content.trim() || saving}
                  className="btn-comic-primary flex-1 disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save Note"}
                </button>
                {notes.length > 0 && (
                  <button
                    onClick={() => setView("library")}
                    className="btn-comic-ghost"
                  >
                    Back
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function NoteCard({ note, onDelete }: { note: EpisodeNote; onDelete: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="retro-border bg-card p-3 space-y-1.5">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <p className={`font-mono text-xs ${expanded ? "" : "line-clamp-2"}`}>
            {note.content}
          </p>
        </div>
        <button
          onClick={() => onDelete(note.id)}
          className="p-1 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
          title="Delete note"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <div className="flex items-center gap-2 font-mono text-[9px] text-muted-foreground">
        {note.timestamp_seconds !== null && (
          <span className="text-primary font-bold">⏱ {formatTimestamp(note.timestamp_seconds)}</span>
        )}
        <span>{new Date(note.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
      </div>
    </div>
  );
}
